import { useCallback } from 'react';

import {
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
} from '@frontierag/frontier-ui-library';
import { CancelOutlined as ClearIcon } from '@frontierag/frontier-ui-library/icons';
import { useField } from 'formik';

import { styles } from './styles';
import type { ITextField } from './types';

export default function TextField({
  name,
  variant = 'outlined',
  disabled,
  InputProps,
  type = 'text',
  onBlur,
  handleChangeCb,
  additionalEndAdornment,
  skipArithmeticSign = false,
  ...props
}: ITextField) {
  const [{ value }, meta, { setValue, setTouched }] = useField(name);

  const handleOnChange = useCallback(
    async (event) => {
      let value = event.target.value;

      if (type === 'number') {
        value = !isNaN(value) && value !== '' ? Number(value) : value;
      }

      await setValue(value);
      if (handleChangeCb) handleChangeCb(value);
    },
    [setValue, type, handleChangeCb],
  );

  const handleOnBlur = useCallback(
    async (event) => {
      if (!meta.touched) await setTouched(true, true);
      if (onBlur) onBlur(event);
    },
    [setTouched, meta.touched, onBlur],
  );

  const handleWheel = useCallback((event) => {
    return event.target.blur(); // Prevent change on scroll inside the number field
  }, []);

  const hasError = Boolean(meta.touched && meta.error);
  const helperText = hasError ? meta.error : '';

  const onClear = useCallback(async () => {
    await setValue('');
    if (!meta.touched) await setTouched(true, true);

    if (handleChangeCb) handleChangeCb('');
  }, [setValue, handleChangeCb, meta.touched, setTouched]);

  const iProps = Object.assign(
    {
      onWheel: type === 'number' ? handleWheel : undefined,
      endAdornment: (
        <>
          {Boolean(value) && !disabled ? (
            <InputAdornment position="end">
              <IconButton onClick={onClear} sx={{ mx: -1 }}>
                <ClearIcon data-testid="btn-clear-field-value" sx={styles.clearIcon} />
              </IconButton>
            </InputAdornment>
          ) : null}
          {additionalEndAdornment}
        </>
      ),
    },
    InputProps,
  );

  const formikValue = type === 'number' && value === 0 ? value : value || '';

  return (
    <MuiTextField
      name={name}
      data-testid={`text-field-${name}`}
      variant={variant}
      disabled={disabled}
      value={formikValue}
      onChange={handleOnChange}
      onBlur={handleOnBlur}
      InputProps={iProps}
      InputLabelProps={{
        shrink: true,
        sx: styles.label,
      }}
      error={hasError}
      helperText={helperText}
      fullWidth
      type={type}
      sx={styles.input}
      onKeyDown={
        skipArithmeticSign
          ? (e) => {
              if (e.key === '-' || e.key === '+') {
                e.preventDefault();
              }
            }
          : undefined
      }
      {...props}
    />
  );
}
